import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';

export interface ClipFrame {
  imageUrl: string;
  timestamp: string;
  cameraName: string;
}

interface DetectionClipPlayerProps {
  frames: ClipFrame[];
  onClose?: () => void;
  timezone?: string;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

const BASE_INTERVAL_MS = 800;
const PRELOAD_BUFFER = 3;

const DetectionClipPlayer: React.FC<DetectionClipPlayerProps> = ({
  frames,
  timezone,
  loading = false,
  error,
  emptyMessage,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'detectionJourneyModal' });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [buffering, setBuffering] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadedIndicesRef = useRef<Set<number>>(new Set());
  const pendingAutoplayRef = useRef(false);
  const preloadImagesRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
    setCurrentIdx(0);
    setPlaying(false);
    setSpeed(1);
    setBuffering(false);
    loadedIndicesRef.current = new Set();
    pendingAutoplayRef.current = frames.length > 1;
    preloadImagesRef.current = [];

    frames.forEach((frame, index) => {
      const img = new Image();
      const markLoaded = () => {
        loadedIndicesRef.current.add(index);
        if (pendingAutoplayRef.current) {
          const firstFramesReady = Array.from({ length: Math.min(frames.length, PRELOAD_BUFFER) })
            .every((_, idx) => loadedIndicesRef.current.has(idx));
          if (firstFramesReady) {
            pendingAutoplayRef.current = false;
            setPlaying(true);
          }
        }
      };
      img.onload = markLoaded;
      img.onerror = markLoaded;
      img.src = frame.imageUrl;
      preloadImagesRef.current.push(img);
    });
  }, [frames]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!playing || frames.length <= 1) return undefined;

    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        if (prev >= frames.length - 1) {
          setPlaying(false);
          setBuffering(false);
          return prev;
        }
        const next = prev + 1;
        if (loadedIndicesRef.current.has(next)) {
          setBuffering(false);
          return next;
        }
        setBuffering(true);
        return prev;
      });
    }, BASE_INTERVAL_MS / speed);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playing, speed, frames.length]);

  useEffect(() => {
    if (!playing) setBuffering(false);
  }, [playing]);

  const togglePlay = () => {
    if (frames.length <= 1) return;
    if (currentIdx >= frames.length - 1) {
      setCurrentIdx(0);
      setPlaying(true);
    } else {
      setPlaying(value => !value);
    }
  };

  const frame = frames[currentIdx] ?? null;
  const previousFrame = currentIdx > 0 ? frames[currentIdx - 1] : null;
  const cameraChanged = frame && previousFrame && frame.cameraName !== previousFrame.cameraName;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">{t('loadingFrames')}</p>
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (frames.length === 0) return <Alert variant="info">{emptyMessage || t('noFrames')}</Alert>;

  return (
    <>
      <div
        className="position-relative bg-dark rounded overflow-hidden mb-3 d-flex justify-content-center align-items-center"
        style={{ minHeight: 400, maxHeight: '65vh' }}
      >
        {frame && (
          <>
            <img
              src={frame.imageUrl}
              alt={t('frameAlt', { index: currentIdx + 1 })}
              style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
            />

            {buffering && (
              <div
                className="position-absolute top-50 start-50 translate-middle text-center p-3 rounded"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 10 }}
              >
                <Spinner animation="border" size="sm" className="me-2" />
                <span>{t('buffering')}</span>
              </div>
            )}

            {cameraChanged && (
              <Badge bg="warning" text="dark" className="position-absolute top-0 start-0 m-2" style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                <i className="bi bi-camera-fill me-1" />
                {t('cameraChange')}
              </Badge>
            )}

            <div className="position-absolute bottom-0 start-0 m-2 px-2 py-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '0.8rem' }}>
              {timezone ? formatDateTimeInNurseryTimezone(frame.timestamp, timezone) : new Date(frame.timestamp).toLocaleString()}
            </div>
            <div className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '0.8rem' }}>
              {currentIdx + 1} / {frames.length}
            </div>
          </>
        )}
      </div>

      {frames.length > 1 && (
        <>
          <input
            type="range"
            className="form-range mb-3"
            min={0}
            max={frames.length - 1}
            value={currentIdx}
            onChange={event => {
              setPlaying(false);
              setCurrentIdx(Number(event.target.value));
            }}
          />

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" onClick={() => { setPlaying(false); setCurrentIdx(0); }} title={t('firstFrame')}>
                <i className="bi bi-skip-start-fill" />
              </Button>
              <Button variant="outline-secondary" onClick={() => { setPlaying(false); setCurrentIdx(prev => Math.max(0, prev - 1)); }} title={t('previousFrame')}>
                <i className="bi bi-skip-backward-fill" />
              </Button>
              <Button variant="primary" onClick={togglePlay} title={playing ? t('pause') : t('play')} style={{ minWidth: '44px' }}>
                <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} />
              </Button>
              <Button variant="outline-secondary" onClick={() => { setPlaying(false); setCurrentIdx(prev => Math.min(frames.length - 1, prev + 1)); }} title={t('nextFrame')}>
                <i className="bi bi-skip-forward-fill" />
              </Button>
              <Button variant="outline-secondary" onClick={() => { setPlaying(false); setCurrentIdx(frames.length - 1); }} title={t('lastFrame')}>
                <i className="bi bi-skip-end-fill" />
              </Button>
            </ButtonGroup>

            <ButtonGroup size="sm">
              {[0.5, 1, 2, 4].map(value => (
                <Button key={value} variant={speed === value ? 'primary' : 'outline-secondary'} onClick={() => setSpeed(value)}>
                  {value}x
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </>
      )}
    </>
  );
};

export default DetectionClipPlayer;
